"use client";

/**
 * Source reference card with expandable detail.
 *
 * Extracted from the prototype `compass-intake.jsx:814-841`.
 * Replaces `.cs-src` CSS classes with Tailwind utilities.
 */

import { useState } from "react";
import { ArrowUpR, ChevD, ChevR } from "../lib/icons";

/* ---------- types ---------- */

/** Shape of a source entry for SrcCard. */
export interface SrcCardSource {
  /** Display name of the source. */
  name: string;
  /** Optional subtitle (e.g. department, dataset name). */
  sub?: string;
  /** Optional URL to the external source. */
  url?: string;
  /** Publication year or date label. */
  year?: string | number;
  /** Source kind — drives rendering variants. */
  kind: "external" | "catalog" | "org" | "note";
  /** Optional blurb / cited excerpt (shown when expanded). */
  blurb?: string;
  /** Whether this source is the one "used" to produce the value. */
  used?: boolean;
  /** Normalized numeric candidate supported by this source. */
  value?: number;
  /** Display-ready form of `value`, including unit/format. */
  valueLabel?: string;
  /** Author name (for "note" kind). */
  by?: string;
  /** Note body text (for "note" kind). */
  body?: string;
}

export interface SrcCardProps {
  /** The source data to display. */
  source: SrcCardSource;
  /** Letter label (a, b, c…) shown in the circle badge. */
  letter?: string;
  /** Custom tooltip text for the "used" checkmark. */
  usedText?: string;
  /** Larger layout for source-detail rails. */
  variant?: "compact" | "detail";
  /** Whether expandable details are initially visible. */
  defaultExpanded?: boolean;
  /** Applies this source's candidate value to the selected assumption. */
  onUseValue?: (source: SrcCardSource) => void;
}

/* ---------- helpers ---------- */

function initialsOf(by: string): string {
  const s = (by || "").trim();
  if (!s) return "";
  const p = s.replace(/\./g, "").split(/\s+/).filter(Boolean);
  const first = p[0]?.[0] ?? "";
  const second = p[1]?.[0] ?? "";
  return (first + second).toUpperCase();
}

/* ---------- component ---------- */

/**
 * A compact source reference card. Shows name, year, and an optional
 * expandable section with an external URL, cited excerpt, or vault excerpt.
 */
export function SrcCard({
  source: s,
  letter,
  usedText = "Reference used to produce this value.",
  variant = "compact",
  defaultExpanded = false,
  onUseValue,
}: SrcCardProps) {
  const [open, setOpen] = useState(defaultExpanded);

  const isNote = s.kind === "note";
  const isVault = s.kind === "catalog";
  const isOrg = s.kind === "org";
  const canExpand = !isOrg && !isNote && (!!s.url || !!s.blurb);

  return (
    <div
      className={[
        "rounded-(--radius-container) border border-(--border-subtle)",
        "bg-(--bg-white)",
        variant === "detail"
          ? "text-[13px] leading-[1.45]"
          : "text-[12.5px] leading-[1.45]",
        isNote ? "border-dashed" : "",
        open ? "pb-0" : "",
      ].join(" ")}
    >
      {/* Header row */}
      <div
        className={[
          "flex items-center gap-2",
          variant === "detail" ? "px-3 py-3" : "px-2.5 py-2",
        ].join(" ")}
      >
        {/* Letter badge or note avatar */}
        <div
          className={[
            "flex items-center justify-center shrink-0",
            variant === "detail"
              ? "h-11 w-11 rounded-(--radius-field) text-[13px]"
              : "h-[22px] w-[22px] rounded-full text-[10px]",
            "font-semibold leading-none",
            s.used
              ? "bg-(--brand-navy) text-(--fg-inverse)"
              : "bg-(--bg-canvas) text-(--fg-secondary) border border-(--border-subtle)",
          ].join(" ")}
          title={isNote ? (s.by ?? "") : undefined}
        >
          {isNote ? initialsOf(s.by ?? "") : letter}
        </div>

        {/* Used checkmark */}
        {s.used && (
          <span
            className={[
              "inline-flex items-center justify-center",
              "h-[14px] w-[14px] rounded-full",
              "bg-(--status-success) text-white",
              "text-[8px]",
              variant === "detail" ? "-ml-5 -mt-6" : "-ml-3.5 -mt-3",
              "cursor-help",
            ].join(" ")}
            title={usedText}
          >
            ✓
          </span>
        )}

        {/* Name */}
        <div
          className={[
            "min-w-0 flex-1 text-(--fg-primary) font-medium",
            variant === "detail" ? "text-[14px] leading-[1.35]" : "truncate",
          ].join(" ")}
        >
          {s.name}
        </div>

        {/* Year */}
        {!isOrg && s.year && (
          <div className="shrink-0 text-(--fg-muted) text-[11px]">
            · {s.year}
          </div>
        )}

        {s.value !== undefined && onUseValue && (
          <button
            type="button"
            className="shrink-0 rounded-(--radius-field) border border-(--fg-brand) bg-(--bg-white) px-2.5 py-1.5 text-[11px] font-semibold text-(--fg-brand) hover:bg-(--bg-hover)"
            onClick={() => onUseValue(s)}
          >
            Use {s.valueLabel ?? s.value}
          </button>
        )}

        {/* Expand toggle */}
        {canExpand && (
          <button
            type="button"
            className={[
              "flex items-center justify-center shrink-0",
              variant === "detail"
                ? "h-8 w-8 rounded-(--radius-field)"
                : "h-[20px] w-[20px] rounded-[3px]",
              "text-(--fg-muted) hover:text-(--fg-secondary)",
              "hover:bg-(--bg-hover)",
              "transition-colors duration-150",
            ].join(" ")}
            onClick={() => setOpen((o) => !o)}
            title={open ? "Hide link" : "Show link"}
            aria-expanded={open}
          >
            {open ? <ChevD size={16} /> : <ChevR size={16} />}
          </button>
        )}
      </div>

      {/* Note body */}
      {isNote && s.body && (
        <div
          className={[
            "text-(--fg-secondary) italic",
            variant === "detail"
              ? "px-3 pb-3 text-[13px]"
              : "px-2.5 pb-2 text-[12px]",
          ].join(" ")}
        >
          "{s.body}"
        </div>
      )}

      {/* Org subtitle */}
      {isOrg && (s.sub || s.year) && (
        <div
          className={[
            "text-(--fg-muted) text-[11px]",
            variant === "detail" ? "px-3 pb-3" : "px-2.5 pb-2",
          ].join(" ")}
        >
          {s.sub}
          {s.sub && s.year ? " · " : ""}
          {s.year}
        </div>
      )}

      {/* Expandable detail */}
      {open && canExpand && (
        <div
          className={[
            "border-t border-(--border-subtle)",
            variant === "detail" ? "mx-3 px-0 py-3" : "px-2.5 py-2",
          ].join(" ")}
        >
          {isVault ? (
            <div
              className={
                variant === "detail"
                  ? "text-[13px] leading-[1.45] text-(--fg-secondary)"
                  : "text-[11.5px] text-(--fg-secondary)"
              }
            >
              <span className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-(--fg-muted) mr-1.5">
                Vault excerpt
              </span>
              {s.blurb}
            </div>
          ) : (
            <>
              {s.blurb && (
                <div
                  className={
                    variant === "detail"
                      ? "mb-2 text-[13px] leading-[1.45] text-(--fg-secondary)"
                      : "mb-1.5 text-[11.5px] text-(--fg-secondary)"
                  }
                >
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-(--fg-muted) mr-1.5">
                    Cited excerpt
                  </span>
                  {s.blurb}
                </div>
              )}
              {s.url && (
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className={[
                    "flex items-center gap-1",
                    variant === "detail"
                      ? "text-[13px] text-(--fg-secondary)"
                      : "text-[11px] text-(--fg-brand)",
                    "hover:underline",
                    "truncate",
                  ].join(" ")}
                >
                  {s.url}
                  <ArrowUpR size={12} />
                </a>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
